import { useTranslation } from 'react-i18next';

import { Globe2Icon, ImageIcon, InfoIcon, LockKeyholeIcon, type LucideIcon } from 'lucide-react';

import type { ResourceSubType } from '@/features/permissions/resource-permissions';
import { cn } from '@/lib/utils';

import type { EntityAccessLevel, PermissionsType } from '../types/types';
import {
  ACCESS_LEVEL_BADGE_CLASS_NAMES,
  ACCESS_LEVEL_LABEL,
  AccessLevelBadge,
} from './access-level-badge';

const PERMISSION_AREAS = [
  { key: 'general', labelKey: 'general', icon: InfoIcon },
  { key: 'media', labelKey: 'media', icon: ImageIcon },
  { key: 'documentsInternal', labelKey: 'access-internal', icon: LockKeyholeIcon },
  { key: 'documentsExternal', labelKey: 'access-external', icon: Globe2Icon },
] satisfies { key: ResourceSubType; labelKey: string; icon: LucideIcon }[];

type PermissionSummaryProps = {
  access: EntityAccessLevel;
  permissions?: PermissionsType;
};

// Shows as much of the granted access as the row has room for (needs an @container parent):
// every area with its label, every area as an icon, or only the general access level.
export const PermissionSummary = ({ access, permissions }: PermissionSummaryProps) => {
  const { t } = useTranslation();

  if (!permissions) return <AccessLevelBadge access={access} />;

  const areas = PERMISSION_AREAS.map((area) => {
    const level = permissions[area.key];
    const label = t(area.labelKey);

    return {
      ...area,
      level,
      label,
      title: `${label}: ${t(ACCESS_LEVEL_LABEL[level])}`,
    };
  });

  return (
    <>
      <span className="hidden items-center gap-1.5 @[46rem]:flex">
        {areas.map(({ key, icon: Icon, level, label, title }) => (
          <span
            key={key}
            title={title}
            className={cn(badgeClassName, 'gap-1 px-[6px]', ACCESS_LEVEL_BADGE_CLASS_NAMES[level])}
          >
            <Icon aria-hidden="true" className="size-3 shrink-0" />
            <span className="font-semibold">{label}</span>
            <span className="opacity-80">{t(ACCESS_LEVEL_LABEL[level])}</span>
          </span>
        ))}
      </span>

      <span className="hidden items-center gap-1 @[28rem]:flex @[46rem]:hidden">
        {areas.map(({ key, icon: Icon, level, title }) => (
          <span
            key={key}
            title={title}
            className={cn(
              badgeClassName,
              'size-6 justify-center p-0',
              ACCESS_LEVEL_BADGE_CLASS_NAMES[level]
            )}
          >
            <Icon aria-hidden="true" className="size-3.5" />
            <span className="sr-only">{title}</span>
          </span>
        ))}
      </span>

      <AccessLevelBadge access={access} className="@[28rem]:hidden" />
    </>
  );
};

const badgeClassName =
  'inline-flex items-center rounded-[4px] border py-[2px] text-[10px] font-medium leading-[15px]';
